import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Draggable from 'react-draggable';

// Debounce utility for resize handling
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const WeatherLegend = ({ legends = [], timeStep = 'current', visible = true }) => {
  const legendRef = useRef();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [hasBeenDragged, setHasBeenDragged] = useState(false);
  const hasBeenDraggedRef = useRef(false);
  const [mapContainer, setMapContainer] = useState(null);

  // Constants for positioning
  const LEGEND_WIDTH = 240;
  const LEGEND_MARGIN = 20;
  const DEFAULT_POSITION = { x: 0, y: 0 };
  const LEGEND_DEFAULT_VERSION = 3; // 🎯 Version control for default position changes

  // Get map container from global reference
  const getMapContainer = useCallback(() => {
    // If global ref doesn't exist, try to find the map container directly
    if (!window.MAP_CONTAINER_REF?.current) {
      const mapContainer = document.querySelector('.dashboard-map-container');
      const mapCore = document.querySelector('[id*="map"]');
      const anyMapDiv = document.querySelector('div[style*="height"]');
      
      // Return the first valid container we find
      return mapContainer || mapCore || anyMapDiv;
    }
    
    return window.MAP_CONTAINER_REF?.current;
  }, []);

  // Calculate default position relative to map container
  const calculateDefaultPosition = useCallback(() => {
    const container = getMapContainer();
    if (!container) {
      return DEFAULT_POSITION;
    }

    const containerRect = container.getBoundingClientRect();
    // Position at top-right of map container with margin
    return {
      x: containerRect.width - LEGEND_WIDTH - LEGEND_MARGIN + 15,  // 🎯 Moved 15px right from edge
      y: LEGEND_MARGIN + 40  // 🎯 Moved 40px downward from top (20px + 10px + 10px)
    };
  }, []);

  // Calculate drag bounds relative to map container
  const calculateDragBounds = useCallback(() => {
    const container = getMapContainer();
    if (!container) {
      return { left: 0, top: 0, right: 0, bottom: 0 };
    }

    const containerRect = container.getBoundingClientRect();
    return {
      left: 0,
      top: 0,
      right: containerRect.width - LEGEND_WIDTH,
      bottom: containerRect.height - 100 // Leave space for legend height
    };
  }, []);

  // Save position to localStorage with version
  const savePosition = (pos) => {
    try {
      const positionWithVersion = {
        ...pos,
        version: LEGEND_DEFAULT_VERSION
      };
      localStorage.setItem('weatherLegendPosition', JSON.stringify(positionWithVersion));
    } catch (e) {
      console.warn('Failed to save weather legend position:', e);
    }
  };

  // Load position from localStorage with version validation
  const getSavedPosition = () => {
    try {
      const saved = localStorage.getItem('weatherLegendPosition');
      if (!saved) return null;
      
      const parsedPosition = JSON.parse(saved);
      
      // Check if saved position matches current default version
      if (parsedPosition.version === LEGEND_DEFAULT_VERSION) {
        return parsedPosition; // Valid version, use saved position
      } else {
        // Invalid or missing version, force reset to new default
        return null;
      }
    } catch (e) {
      console.warn('Failed to parse saved weather legend position:', e);
      return null;
    }
  };

  // Initialize map container and position
  useEffect(() => {
    const tryGetContainer = () => {
      const container = getMapContainer();
      if (container) {
        setMapContainer(container);
        
        // Set initial position
        const savedPosition = getSavedPosition();
        if (savedPosition) {
          setPosition(savedPosition);
          setHasBeenDragged(true);
          hasBeenDraggedRef.current = true;
        } else {
          const defaultPos = calculateDefaultPosition();
          setPosition(defaultPos);
        }
        return true; // Success
      }
      return false; // Failed
    };
    
    // Try immediately
    if (!tryGetContainer()) {
      // Try again after a short delay for DOM to be ready
      const timeout = setTimeout(() => {
        if (!tryGetContainer()) {
          // One more try after longer delay
          setTimeout(() => {
            tryGetContainer();
          }, 500);
        }
      }, 100);
      
      return () => clearTimeout(timeout);
    }
  }, []); // Empty dependencies - run once on mount

  // Handle map container resize
  useEffect(() => {
    const handleResize = debounce(() => {
      const container = getMapContainer();
      if (!container) return;

      if (!hasBeenDraggedRef.current) {
        // Reset to default position if user hasn't customized
        const defaultPos = calculateDefaultPosition();
        setPosition(defaultPos);
      } else {
        // ALWAYS reposition to right edge on resize (preserve Y position)
        const bounds = calculateDragBounds();
        const defaultPos = calculateDefaultPosition();
        
        setPosition(prevPos => {
          // Always use new right-edge X position, but preserve user's Y preference
          return {
            x: defaultPos.x, // Always anchor to right edge
            y: Math.min(Math.max(bounds.top, prevPos.y), bounds.bottom) // Constrain Y within bounds
          };
        });
      }
    }, 300);

    // Listen for window resize which affects map container
    window.addEventListener('resize', handleResize);
    
    // Also listen for map container changes via ResizeObserver if available
    if (mapContainer && window.ResizeObserver) {
      const resizeObserver = new ResizeObserver((entries) => {
        handleResize();
      });
      resizeObserver.observe(mapContainer);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        resizeObserver.disconnect();
      };
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [mapContainer, calculateDefaultPosition, calculateDragBounds]);

  // Handle drag stop
  const handleDragStop = useCallback((e, data) => {
    const newPosition = { x: data.x, y: data.y };
    
    // Constrain position within map container bounds
    const bounds = calculateDragBounds();
    const constrainedPosition = {
      x: Math.min(Math.max(bounds.left, newPosition.x), bounds.right),
      y: Math.min(Math.max(bounds.top, newPosition.y), bounds.bottom)
    };
    
    // Save and update position
    savePosition(constrainedPosition);
    setPosition(constrainedPosition);
    setHasBeenDragged(true);
    hasBeenDraggedRef.current = true;
  }, [calculateDragBounds]);

  // Handle double-click to reset position
  const handleDoubleClick = useCallback(() => {
    localStorage.removeItem('weatherLegendPosition');
    const defaultPos = calculateDefaultPosition();
    setPosition(defaultPos);
    setHasBeenDragged(false);
    hasBeenDraggedRef.current = false;
  }, [calculateDefaultPosition]);

  // Hide if not visible or no legends
  if (!visible || legends.length === 0) {
    return null;
  }

  // Don't render if map container not available
  if (!mapContainer) {
    return null;
  }

  const timeStepLabels = {
    current: 'Now',
    plus3h: '+3h',
    plus6h: '+6h'
  };

  const dragBounds = calculateDragBounds();

  // Render legend using Portal inside map container
  return createPortal(
    <Draggable
      position={position}
      onStop={handleDragStop}
      handle=".legend-drag-handle"
      bounds={dragBounds}
    >
      <div 
        ref={legendRef}
        style={{
          position: 'absolute',
          zIndex: 1000,
          width: `${LEGEND_WIDTH}px`,
          maxHeight: '400px',
          pointerEvents: 'auto',
          userSelect: 'none'
        }}
      >
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #e8e8e8',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          padding: '12px',
          fontSize: '12px',
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          {/* Draggable Header */}
          <div 
            className="legend-drag-handle"
            style={{ 
              marginBottom: '12px', 
              borderBottom: '1px solid #f0f0f0', 
              paddingBottom: '6px',
              cursor: 'move',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onDoubleClick={handleDoubleClick}
            title="Drag to move • Double-click to reset position"
          >
            <div style={{ fontSize: '13px', color: '#1890ff', fontWeight: 'bold' }}>
              🌦️ Weather Overlay ({timeStepLabels[timeStep]})
            </div>
            <div style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>
              ⋮⋮
            </div>
          </div>

          {/* Content */}
          <div>
            {legends.map((legend) => (
              <div key={legend.type} style={{ marginBottom: '12px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  {legend.name}
                </div>
                {legend.items && legend.items.map((item, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    marginBottom: '2px'
                  }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: item.color,
                      border: '1px solid #ddd',
                      borderRadius: '2px'
                    }} />
                    <span style={{ fontSize: '11px' }}>{item.label}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          
          {/* Footer */}
          <div style={{ 
            marginTop: '8px', 
            paddingTop: '6px', 
            borderTop: '1px solid #f0f0f0',
            fontSize: '10px',
            color: '#999'
          }}>
            OpenWeatherMap
          </div>
        </div>
      </div>
    </Draggable>,
    mapContainer
  );
};

export default WeatherLegend;